import { LabeledField } from '@/components/form/labeled-field';
import NumericTextField from '@/components/form/numeric-text-field';
import { invalidNumberHelpText } from '@/components/form/validation-help-text';
import { Flex, Text } from '@lego/klik-ui';
import type {
  PriceUnitCalculationSettings,
  PriceUnitSettingsTouchedField,
  SimulationSettingsValidation,
} from '../types';
import { linkedEnhancer } from './linked-enhancer';
import { ThresholdField } from './threshold-field';

type SimulationSettingsSectionProps = {
  simulationSettings: PriceUnitCalculationSettings;
  isSimulationSettingsInherited: boolean;
  validation: SimulationSettingsValidation;
  onFieldChange: (field: string, value: string | boolean | undefined) => void;
  onFieldBlur: (field: PriceUnitSettingsTouchedField) => void;
};

export const SimulationSettingsSection = ({
  simulationSettings,
  isSimulationSettingsInherited,
  validation,
  onFieldChange,
  onFieldBlur,
}: SimulationSettingsSectionProps) => {
  const {
    isInvalidTargetLpIndex,
    isInvalidLpFmcTarget,
    isInvalidThreshold,
    isInvalidAboveThreshold,
    isInvalidBelowThreshold,
  } = validation;
  return (
    <>
      <Flex flexDirection="column" gap={2} width="100%">
        <Text className="label-lg-medium">Simulation settings</Text>
      </Flex>

      <Flex
        display="grid"
        gridTemplateColumns="repeat(5, minmax(0, 1fr))"
        gap={4}
        width="100%"
      >
        <LabeledField
          label="Target LP Index"
          tooltipText="Target LP index value as a percentage of the price. Enter 100 for 100% of the price."
          helpText={isInvalidTargetLpIndex ? invalidNumberHelpText : undefined}
        >
          <NumericTextField
            disabled={isSimulationSettingsInherited}
            value={simulationSettings.targetLpIndex}
            validationState={isInvalidTargetLpIndex ? 'invalid' : undefined}
            onChange={(_e, value) => onFieldChange('targetLpIndex', value)}
            onBlur={() => onFieldBlur('targetLpIndex')}
            renderEndEnhancer={linkedEnhancer(isSimulationSettingsInherited)}
            required
          />
        </LabeledField>

        <LabeledField
          label="LP/FMC Target"
          helpText={isInvalidLpFmcTarget ? invalidNumberHelpText : undefined}
        >
          <NumericTextField
            disabled={isSimulationSettingsInherited}
            validationState={isInvalidLpFmcTarget ? 'invalid' : undefined}
            value={simulationSettings.lpFmcTarget}
            onChange={(_e, value) => onFieldChange('lpFmcTarget', value)}
            onBlur={() => onFieldBlur('lpFmcTarget')}
            renderEndEnhancer={linkedEnhancer(isSimulationSettingsInherited)}
            required
          />
        </LabeledField>

        <LabeledField
          label="Price Threshold"
          helpText={isInvalidThreshold ? invalidNumberHelpText : undefined}
        >
          <NumericTextField
            disabled={isSimulationSettingsInherited}
            validationState={isInvalidThreshold ? 'invalid' : undefined}
            value={simulationSettings.threshold}
            onChange={(_e, value) => onFieldChange('threshold', value)}
            onBlur={() => onFieldBlur('threshold')}
            renderEndEnhancer={linkedEnhancer(isSimulationSettingsInherited)}
            required
          />
        </LabeledField>

        <ThresholdField
          label="Above price threshold"
          minValue={simulationSettings.aboveThresholdMin}
          maxValue={simulationSettings.aboveThresholdMax}
          idPrefix="above"
          disabled={isSimulationSettingsInherited}
          hasError={isInvalidAboveThreshold}
          isLinked={isSimulationSettingsInherited}
          onMinChange={(value) => onFieldChange('aboveThresholdMin', value)}
          onMaxChange={(value) => onFieldChange('aboveThresholdMax', value)}
          onFieldBlur={onFieldBlur}
        />

        <ThresholdField
          label="Below price threshold"
          minValue={simulationSettings.belowThresholdMin}
          maxValue={simulationSettings.belowThresholdMax}
          idPrefix="below"
          disabled={isSimulationSettingsInherited}
          hasError={isInvalidBelowThreshold}
          isLinked={isSimulationSettingsInherited}
          onMinChange={(value) => onFieldChange('belowThresholdMin', value)}
          onMaxChange={(value) => onFieldChange('belowThresholdMax', value)}
          onFieldBlur={onFieldBlur}
        />
      </Flex>
    </>
  );
};
